import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config';

const AllergyReviewModal = ({ isOpen, onClose, onSave, isDarkMode }) => {
    const [family, setFamily] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const commonAllergens = [
        'Yok', 'Gluten', 'Süt', 'Yumurta', 'Fındık', 'Fıstık', 'Balık', 'Soya', 'Susam'
    ];

    useEffect(() => {
        if (isOpen) {
            fetchFamilyData();
        }
    }, [isOpen]);

    const fetchFamilyData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/allergen-kitchen/family-info`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setFamily(res.data.family);
            }
        } catch (error) {
            console.error('Family info fetch error:', error);
            Swal.fire('Hata', 'Aile bilgileri alınamadı.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const toggleAllergen = (type, memberId, allergen) => {
        const newFamily = { ...family };
        const isYok = allergen.toLowerCase().includes('yok');

        const updateList = (current) => {
            if (isYok) {
                // If "Yok" is selected, clear everything else and keep "Yok"
                // If "Yok" is already selected and clicked again, clear it (empty)
                return current.includes(allergen) ? [] : [allergen];
            } else {
                // If a normal allergen is selected, remove "Yok" from the list
                const filtered = current.filter(a => !a.toLowerCase().includes('yok'));
                return filtered.includes(allergen)
                    ? filtered.filter(a => a !== allergen)
                    : [...filtered, allergen];
            }
        };

        if (type === 'user') {
            newFamily.user.allergies = updateList(newFamily.user.allergies || []);
        } else if (type === 'partner') {
            newFamily.partner.allergies = updateList(newFamily.partner.allergies || []);
        } else if (type === 'child') {
            newFamily.children = newFamily.children.map(c =>
                c.id === memberId
                    ? { ...c, allergies: updateList(c.allergies || []) }
                    : c
            );
        }
        setFamily(newFamily);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                userAllergies: family.user.allergies,
                partnerAllergies: family.partner?.allergies || [],
                childrenData: family.children
            };
            const res = await onSave(payload);
            if (res.success) {
                onClose();
            }
        } catch (error) {
            Swal.fire('Hata', 'Kaydedilirken bir hata oluştu.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] p-4 font-display">
            <div className="bg-white dark:bg-slate-900 rounded-[3rem] max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col border border-slate-200 dark:border-slate-800 animate-in zoom-in duration-300">

                {/* Header */}
                <div className="p-8 pb-6 relative">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-500/20">
                            <span className="material-symbols-outlined text-white text-xl">verified_user</span>
                        </div>
                        <div>
                            <span className="text-[10px] font-black tracking-[0.2em] uppercase text-red-500 block">PREMIUM ACCESS</span>
                            <h2 className="text-2xl font-black text-slate-900 dark:text-white">Alerji <span className="text-red-500 italic">Doğrulama</span></h2>
                        </div>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed max-w-md">
                        Alerjen Mutfak, girdiğiniz bilgilere göre tarifleri %100 güvenlik filtresinden geçirir.
                    </p>
                    <button onClick={onClose} className="absolute top-8 right-8 w-10 h-10 rounded-full flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-8 pb-8 space-y-8 scrollbar-hide">
                    {loading ? (
                        <div className="py-20 flex flex-col items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-red-500 mb-4"></div>
                            <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Veriler Hazırlanıyor...</p>
                        </div>
                    ) : family && (
                        <>
                            {/* User Section */}
                            <MemberCard
                                key="member-user"
                                member={family.user}
                                type="user"
                                onToggle={toggleAllergen}
                                commonAllergens={commonAllergens}
                                icon="person"
                                color="blue"
                            />

                            {/* Partner Section */}
                            {family.partner && (
                                <MemberCard
                                    key="member-partner"
                                    member={family.partner}
                                    type="partner"
                                    onToggle={toggleAllergen}
                                    commonAllergens={commonAllergens}
                                    icon="favorite"
                                    color="pink"
                                />
                            )}

                            {/* Children Section */}
                            {family.children.length > 0 && family.children.map((child, idx) => (
                                <MemberCard
                                    key={`child-${child.id || idx}`}
                                    member={child}
                                    type="child"
                                    onToggle={toggleAllergen}
                                    commonAllergens={commonAllergens}
                                    icon="child_care"
                                    color="amber"
                                />
                            ))}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-8 pt-6 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800">
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="w-full h-16 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-black rounded-[1.5rem] shadow-xl shadow-red-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {saving ? (
                            <span className="animate-pulse">Kaydediliyor...</span>
                        ) : (
                            <>
                                <span className="material-symbols-outlined">check_circle</span>
                                <span>Bilgileri Doğrula & Menüye Geç</span>
                            </>
                        )}
                    </button>
                    <p className="text-center text-[10px] text-slate-400 mt-4 font-bold uppercase tracking-widest">
                        Bu bilgiler KitchenPlanner 7 günlük sürecinden muaftır.
                    </p>
                </div>
            </div>
        </div>
    );
};

const MemberCard = ({ member, type, onToggle, commonAllergens, icon, color }) => {
    const colorClasses = {
        blue: 'from-blue-500 to-indigo-600 text-blue-500 bg-blue-50 dark:bg-blue-900/10',
        pink: 'from-pink-500 to-rose-600 text-pink-500 bg-pink-50 dark:bg-pink-900/10',
        amber: 'from-amber-500 to-orange-600 text-amber-500 bg-amber-50 dark:bg-amber-900/10'
    };

    const bgClass = color === 'blue' ? 'bg-blue-500/10' : color === 'pink' ? 'bg-pink-500/10' : 'bg-amber-500/10';
    const textClass = color === 'blue' ? 'text-blue-500' : color === 'pink' ? 'text-pink-500' : 'text-amber-500';

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${bgClass}`}>
                    <span className={`material-symbols-outlined text-[18px] ${textClass}`}>{icon}</span>
                </div>
                <h3 className="font-black text-slate-800 dark:text-slate-100 text-sm uppercase tracking-tight">{member.name}</h3>
            </div>

            <div className="flex flex-wrap gap-2">
                {commonAllergens.map(allergen => {
                    const isActive = (member.allergies || []).includes(allergen);
                    return (
                        <button
                            key={allergen}
                            onClick={() => onToggle(type, member.id || null, allergen)}
                            className={`px-4 py-2.5 rounded-2xl border-2 text-[11px] font-black transition-all active:scale-95 ${isActive
                                ? 'bg-red-500 border-red-500 text-white shadow-lg shadow-red-500/20'
                                : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-500 hover:border-red-200'}`}
                        >
                            {allergen}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default AllergyReviewModal;
